import React, { useContext, useEffect, useRef } from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { debounce } from '@thd-olt-functional/utils';
import { LegalComplianceMessage } from './drawer/LegalComplianceMessage';
import { AssistantLoader } from './drawer/AssistantLoader';
import { ChatMessage } from './ChatMessage';
import { SuggestedQuestions } from './SuggestedQuestions';
import { useAssistant } from '../AssistantProvider';
import { useDrawer } from '../DrawerProvider';
import { TryAgainButton } from './TryAgainButton';
import { HistoryClearedMessage } from './drawer/HistoryClearedMessage';
import { MESSAGE_ORIGIN_PREPOPULATED, MESSAGE_ORIGIN_RETRY, RETRY_MESSAGE } from '../constants';

const MAX_OBSERVATION_COUNT = 5;
const HEIGHT_CHANGE_THRESHOLD_PX = 50;
const OBSERVER_TIMEOUT_MS = 8000;

export const DrawerContent = () => {
  const { channel } = useContext(ExperienceContext);
  const {
    messages,
    historyLoading,
    askUserQuestion,
    botLoading,
    saveMessage,
  } = useAssistant();
  const { scrollToBottom, scrollEl } = useDrawer();

  const lastUserMessageRef = useRef(null);
  const hasInitiallyLoaded = useRef(false);

  const isMobile = channel === 'mobile';
  const lastBotMessage = messages.findLast(({ type }) => type === 'bot');
  const lastUserMessage = messages.findLast(({ type }) => type === 'user');
  const lastAtcConfirmationIndex = messages.findLastIndex(({ type }) => type === 'add-to-cart');
  const isLastMessageATCConfirmation = lastAtcConfirmationIndex === messages.length - 1;
  const isLastMessageFromUser = lastUserMessage && lastUserMessage === messages[messages.length - 1];

  const showRetryPrompt = isLastMessageFromUser && !botLoading;
  const showSuggestedQuestions = !botLoading && !showRetryPrompt && !isLastMessageFromUser;

  useEffect(() => {
    // Scrolls to bottom on resize. Primarily needed for mobile minimized to open state.
    const debouncedSTB = debounce(100, () => scrollToBottom());

    window.addEventListener('resize', debouncedSTB);

    return () => window.removeEventListener('resize', debouncedSTB);
  }, []);

  useEffect(() => {
    let mutationObserver;
    // Messages should be greater than 1 to avoid this to run when there is only the SSR initial message
    if (!historyLoading && !hasInitiallyLoaded.current && messages.length > 1) {
      // This was scrolling the main page as well on load, timeout seems to only scroll the drawer.
      setTimeout(() => {
        scrollToBottom();
        hasInitiallyLoaded.current = true;
        let initialHeight = scrollEl.current.scrollHeight;
        let observationCount = 0;
        const initialMessageCount = messages.length;
        // Set up MutationObserver to check drawer height changes due to dynamic content and be able to scroll to bottom
        mutationObserver = new MutationObserver(() => {
          if (messages.length !== initialMessageCount || observationCount >= MAX_OBSERVATION_COUNT) {
            mutationObserver.disconnect();
            return;
          }

          const currentHeight = scrollEl.current.scrollHeight;

          if (currentHeight > initialHeight + HEIGHT_CHANGE_THRESHOLD_PX) {
            scrollToBottom();
            initialHeight = currentHeight;
            observationCount += 1;
          }
        });

        mutationObserver.observe(scrollEl.current, {
          childList: true,
          subtree: true,
        });

        setTimeout(() => {
          mutationObserver.disconnect();
        }, OBSERVER_TIMEOUT_MS);
      }, 1);
    }

    return () => {
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
    };
  }, [historyLoading, messages.length]);

  useEffect(() => {
    if (historyLoading || !hasInitiallyLoaded.current) {
      return;
    }

    // Case 1: After user sends message - center the message
    if (!isLastMessageATCConfirmation && lastUserMessageRef.current) {
      const messagePosition = lastUserMessageRef.current.offsetTop;
      const drawerHeight = scrollEl.current.clientHeight;
      const messageHeight = lastUserMessageRef.current.offsetHeight;

      const centeredPosition = messagePosition - (drawerHeight / 2) + (messageHeight / 2);
      const maxScrollTop = scrollEl.current.scrollHeight - scrollEl.current.clientHeight;

      scrollEl.current.scrollTo({
        top: Math.max(0, Math.min(centeredPosition, maxScrollTop)),
        behavior: 'smooth'
      });
      return;
    }

    // Case 2: Add to cart confirmation - scroll to bottom
    if (isLastMessageATCConfirmation) {
      scrollToBottom();
    }
  }, [historyLoading, messages, isLastMessageATCConfirmation]);

  useEffect(() => {
    if (botLoading) {
      scrollToBottom();
    }
  }, [botLoading]);

  return (
    <>
      <div data-testid="drawer-message-history">
        <LegalComplianceMessage isMobile={isMobile} />
        <HistoryClearedMessage />
        <div className="sui-flex sui-flex-col">
          {messages?.map((message, i) => {
            const showFeedback = (
              message.type === 'bot'
              && !message.feedbackProvided
              && message === lastBotMessage
              && i !== 0
            );
            return (
              <ChatMessage
                key={i}
                message={message}
                showFeedback={showFeedback}
                ref={message === lastUserMessage ? lastUserMessageRef : null}
              />
            );
          })}
          {showRetryPrompt && (
            <>
              <ChatMessage
                message={RETRY_MESSAGE}
                showFeedback={false}
              />
              <TryAgainButton
                value={lastUserMessage.content}
                onClick={(question) => {
                  askUserQuestion({ userQuestion: question, messageOrigin: MESSAGE_ORIGIN_RETRY });
                }}
              />
            </>
          )}
        </div>
        {botLoading && <AssistantLoader />}
      </div>
      {showSuggestedQuestions && (
        <SuggestedQuestions
          onClick={async (question, suggestedQuestionPosition, isInitialSuggestedQuestion) => {
            askUserQuestion({
              userQuestion: question,
              messageOrigin: MESSAGE_ORIGIN_PREPOPULATED,
              suggestedQuestionPosition,
              isInitialSuggestedQuestion,
            });
          }}
        />
      )}
    </>
  );
};

DrawerContent.propTypes = {};
